<template>
  <div class="ai-review-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <div class="ai-review-container">
      <div class="page-header">
        <h1>AI Review</h1>
        <p class="subtitle">Scan results pending AI review</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading scan results...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="fetchScans">Try Again</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!scans || scans.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>No scan results for AI review.</p>
      </div>

      <!-- Scans Table -->
      <div v-else class="scans-table-wrapper">
        <table class="scans-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Scanned At</th>
              <th>Heuristic Status</th>
              <th>Heuristic Score</th>
              <th>AI Status</th>
              <th>AI Confidence</th>
              <th>Recalc</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="scan in scans"
              :key="scan.id"
              class="clickable-row"
              @click="openPanel(scan)"
            >
              <td class="domain-cell">{{ scan.domainName || 'N/A' }}</td>
              <td class="date-cell">{{ formatDate(scan.scannedAt) }}</td>
              <td class="status-cell">
                <span class="ai-status-badge" :class="getAiStatusClass(scan.heuristicStatus)">
                  {{ scan.heuristicStatus || 'N/A' }}
                </span>
              </td>
              <td class="confidence-cell">{{ formatConfidence(scan.heuristicScore) }}</td>
              <td class="status-cell">
                <span class="ai-status-badge" :class="getAiStatusClass(scan.aiStatus)">
                  {{ scan.aiStatus || 'N/A' }}
                </span>
              </td>
              <td class="confidence-cell">{{ formatConfidence(scan.aiConfidence) }}</td>
              <td class="action-cell" @click.stop>
                <button
                  type="button"
                  class="recalc-btn"
                  :disabled="recalculatingScanId === scan.id"
                  :title="'Recalculate Heuristic score'"
                  @click="recalculateHeuristicScore(scan)"
                >
                  <span v-if="recalculatingScanId === scan.id" class="recalc-spinner"></span>
                  <span v-else>Recalc</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Side Panel Overlay -->
    <div v-if="selectedScan" class="panel-overlay" @click="closePanel"></div>

    <!-- Side Panel -->
    <aside v-if="selectedScan" class="side-panel">
      <div class="panel-header">
        <h2 class="panel-title">{{ selectedScan.domainName || 'N/A' }}</h2>
        <div class="panel-meta">
          <span class="meta-item">Nodes scanned: {{ selectedScan.nodesScanned ?? '—' }}</span>
          <span class="meta-item">Duration: {{ formatDuration(selectedScan.duration) }}</span>
        </div>
        <div v-if="scanStatus || scanConfidence" class="panel-status">
          <span v-if="scanStatus" class="status-badge" :class="getAiStatusClass(scanStatus)">
            Status: {{ scanStatus }}
          </span>
          <span v-if="scanConfidence" class="confidence-badge">
            Heuristic Status: {{ scanConfidence }}
          </span>
        </div>
        <div v-if="aiReason" class="panel-reasoning">
          <div class="reasoning-label">AI Reasoning:</div>
          <div class="reasoning-text">{{ aiReason }}</div>
        </div>
        <div class="panel-totals">
          <span class="total-item">Added ({{ selectedScan.totalAdded ?? 0 }})</span>
          <span class="total-item">Changed ({{ selectedScan.totalChanged ?? 0 }})</span>
          <span class="total-item">Removed ({{ selectedScan.totalRemoved ?? 0 }})</span>
        </div>
        <button class="panel-close" aria-label="Close" @click="closePanel">
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
      <div class="panel-body">
        <!-- Panel loading -->
        <div v-if="panelLoading" class="panel-loading">
          <div class="spinner"></div>
          <p>Loading scan details...</p>
        </div>
        <!-- Panel error -->
        <div v-else-if="panelError" class="panel-error">
          <p>{{ panelError }}</p>
          <button class="retry-btn" @click="fetchPanelData">Try Again</button>
        </div>
        <!-- Added / Changed / Removed files -->
        <div v-else class="result-sections">
          <section class="result-section">
            <h3 class="result-title">Added</h3>
            <ul v-if="addedFiles && addedFiles.length > 0" class="result-items">
              <li v-for="(path, i) in addedFiles" :key="'a-' + i" class="result-item">{{ path }}</li>
            </ul>
            <p v-else class="result-empty">None</p>
          </section>
          <section class="result-section">
            <h3 class="result-title">Changed</h3>
            <ul v-if="changedFiles && changedFiles.length > 0" class="result-items">
              <li v-for="(path, i) in changedFiles" :key="'c-' + i" class="result-item">{{ path }}</li>
            </ul>
            <p v-else class="result-empty">None</p>
          </section>
          <section class="result-section">
            <h3 class="result-title">Removed</h3>
            <ul v-if="removedFiles && removedFiles.length > 0" class="result-items">
              <li v-for="(path, i) in removedFiles" :key="'r-' + i" class="result-item">{{ path }}</li>
            </ul>
            <p v-else class="result-empty">None</p>
          </section>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'

export default {
  name: 'AdminAIReviewView',
  setup() {
    const router = useRouter()
    const scans = ref([])
    const loading = ref(true)
    const error = ref('')
    const selectedScan = ref(null)
    const panelLoading = ref(false)
    const panelError = ref('')
    const addedFiles = ref([])
    const changedFiles = ref([])
    const removedFiles = ref([])
    const scanStatus = ref('')
    const scanConfidence = ref('')
    const aiReason = ref('')
    const recalculatingScanId = ref(null)

    const recalculateHeuristicScore = async (scan) => {
      if (!scan?.domainId || !scan?.id) return
      try {
        recalculatingScanId.value = scan.id
        await api('/admin/recalculate-heuristic-score', {
          method: 'POST',
          body: JSON.stringify({ domainId: scan.domainId, scanId: scan.id })
        }, router)
        await fetchScans()
      } catch (err) {
        console.error('Failed to recalculate heuristic score:', err)
        error.value = err.code || 'Failed to recalculate heuristic score'
      } finally {
        recalculatingScanId.value = null
      }
    }

    const fetchScans = async () => {
      try {
        loading.value = true
        error.value = ''
        const data = await api('/admin/scans-for-review', {}, router)
        scans.value = data?.data || []
      } catch (err) {
        console.error('Failed to fetch scans for review:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatConfidence = (confidence) => {
      if (confidence == null || confidence === '') return '—'
      const num = Number(confidence)
      if (isNaN(num)) return 'N/A'
      return `${num.toFixed(2)}`
    }

    const getAiStatusClass = (status) => {
      if (!status) return ''
      const normalized = (status || '').toLowerCase()
      if (normalized === 'unset') return 'unset'
      if (normalized === 'safe' || normalized === 'most probably safe' || normalized === 'probably safe') return 'safe'
      if (normalized === 'suspicious' || normalized === 'highly suspicious') return 'suspicious'
      if (normalized === 'malicious') return 'malicious'
      return ''
    }

    const formatDuration = (nanos) => {
      if (nanos == null || nanos === '') return '—'
      const sec = Number(nanos) / 1e9
      if (sec < 60) return `${sec.toFixed(1)}s`
      const m = Math.floor(sec / 60)
      const s = (sec % 60).toFixed(1)
      return s === '0.0' ? `${m}m` : `${m}m ${s}s`
    }

    const openPanel = (scan) => {
      selectedScan.value = scan
      panelError.value = ''
      addedFiles.value = []
      changedFiles.value = []
      removedFiles.value = []
      scanStatus.value = ''
      scanConfidence.value = ''
      aiReason.value = ''
      fetchPanelData()
    }

    const closePanel = () => {
      selectedScan.value = null
      panelError.value = ''
      addedFiles.value = []
      changedFiles.value = []
      removedFiles.value = []
      scanStatus.value = ''
      scanConfidence.value = ''
      aiReason.value = ''
    }

    const fetchPanelData = async () => {
      if (!selectedScan.value) return
      try {
        panelLoading.value = true
        panelError.value = ''
        const params = new URLSearchParams({
          domainId: selectedScan.value.domainId,
          scanId: selectedScan.value.id
        })
        const data = await api(`/admin/scan-result-for-review?${params}`, {}, router)
        scanStatus.value = data?.status ?? ''
        scanConfidence.value = data?.confidence ?? ''
        aiReason.value = data?.aiReason ?? ''
        const r = data?.results ?? {}
        addedFiles.value = r.added ?? []
        changedFiles.value = r.changed ?? []
        removedFiles.value = r.removed ?? []
      } catch (err) {
        console.error('Failed to fetch scan result for review:', err)
        panelError.value = err.code || 'Failed to load scan details'
      } finally {
        panelLoading.value = false
      }
    }

    onMounted(() => {
      fetchScans()
    })

    return {
      scans,
      loading,
      error,
      recalculatingScanId,
      recalculateHeuristicScore,
      fetchScans,
      formatDate,
      formatConfidence,
      getAiStatusClass,
      formatDuration,
      selectedScan,
      panelLoading,
      panelError,
      addedFiles,
      changedFiles,
      removedFiles,
      scanStatus,
      scanConfidence,
      aiReason,
      openPanel,
      closePanel,
      fetchPanelData
    }
  }
}
</script>

<style scoped>
.ai-review-page {
  position: relative;
  min-height: 100vh;
  padding: 40px 20px;
  overflow: hidden;
}

/* Background elements */
.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  pointer-events: none;
  animation: pulse-glow 8s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
}

.ai-review-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
  animation: fade-in 0.6s ease-out;
}

.page-header h1 {
  color: var(--text-primary);
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

/* Loading, Error, Empty states */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
  animation: fade-in 0.4s ease-out;
}

.loading-state p,
.error-state p,
.empty-state p {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

.error-state svg,
.empty-state svg {
  color: var(--text-muted);
}

.error-state svg {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.retry-btn {
  padding: 10px 24px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Scans Table */
.scans-table-wrapper {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  animation: slide-up 0.6s ease-out;
}

.scans-table {
  width: 100%;
  border-collapse: collapse;
}

.scans-table thead {
  background: rgba(0, 0, 0, 0.2);
}

.scans-table th {
  padding: 16px;
  text-align: left;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.scans-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease;
}

.scans-table tbody tr.clickable-row {
  cursor: pointer;
}

.scans-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

.scans-table tbody tr:last-child {
  border-bottom: none;
}

.scans-table td {
  padding: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

.domain-cell {
  color: var(--text-primary);
  font-weight: 500;
}

.date-cell {
  color: var(--text-primary);
  font-weight: 500;
}

.status-cell {
  padding: 12px 16px;
}

.confidence-cell {
  color: var(--text-primary);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.action-cell {
  padding: 12px 16px;
  white-space: nowrap;
}

.recalc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recalc-btn:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

.recalc-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.recalc-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.ai-status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.ai-status-badge.unset {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
}

.ai-status-badge.safe {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.ai-status-badge.suspicious {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.ai-status-badge.malicious {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

@keyframes slide-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1024px) {
  .scans-table-wrapper {
    overflow-x: auto;
  }

  .scans-table {
    min-width: 500px;
  }
}

@media (max-width: 767px) {
  .ai-review-page {
    padding: 24px 16px;
  }

  .page-header h1 {
    font-size: 28px;
  }

  .subtitle {
    font-size: 14px;
  }

  .scans-table th,
  .scans-table td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .scans-table th {
    font-size: 12px;
  }
}

/* Side Panel */
.panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1100;
  animation: fade-in 0.2s ease-out;
}

.side-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 55vw;
  max-width: 100vw;
  height: 100vh;
  background: var(--bg-secondary);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  animation: panel-slide-in 0.3s ease-out;
}

@keyframes panel-slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.panel-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  position: relative;
}

.panel-title {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin: 0 36px 0 0;
}

.panel-meta {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.meta-item {
  color: var(--text-secondary);
}

.panel-status {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.status-badge,
.confidence-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
}

.status-badge {
  text-transform: capitalize;
}

.status-badge.unset {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
}

.status-badge.safe {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge.suspicious {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.status-badge.malicious {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.confidence-badge {
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
}

.panel-reasoning {
  margin-top: 16px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}

.reasoning-label {
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.reasoning-text {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.panel-totals {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.total-item {
  color: var(--text-secondary);
}

.panel-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
}

.panel-close:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}

.panel-loading p,
.panel-error p {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
}

.result-sections {
  animation: fade-in 0.3s ease-out;
}

.result-section {
  margin-bottom: 24px;
}

.result-section:last-child {
  margin-bottom: 0;
}

.result-title {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}

.result-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.result-item {
  padding: 10px 12px;
  color: var(--text-secondary);
  font-size: 14px;
  font-family: ui-monospace, monospace;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.result-item:last-child {
  border-bottom: none;
}

.result-empty {
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
  padding: 10px 0;
}
</style>
